import { useCallback, useEffect, useState } from "react";
import { formatPesos } from "../money";
import type { BooksApi, PlaceId, SummaryBody } from "./api";

/**
 * The partners' summary (T035, FR-141, research R8).
 *
 * The page that replaces the workbook file that used to be emailed around:
 * spent to date by category, what is still to spend, where the money is, and
 * who put it in. Printing it — the browser's own dialog — saves it as a PDF,
 * which is why the layout drops every control when printed and keeps only the
 * figures and the date they were read.
 */

const PLACE_NAMES: Record<PlaceId, string> = {
  drawer: "Drawer",
  cash_held: "Cash held",
  ewallet: "E-wallet",
  capital_fund: "Capital fund",
  // Id kept from 0008; the place is the bank account (see 0009).
  treasurer: "Bank",
};

const KIND_NAMES: Record<string, string> = {
  setup: "Setting up",
  goods: "Stock bought",
  running: "Running the café",
  deposit: "Deposits (refundable)",
};

const today = () => new Date().toISOString().slice(0, 10);

/** 3000 → "30%". */
const ownership = (bp: number) => {
  const percent = bp / 100;
  return `${Number.isInteger(percent) ? percent : percent.toFixed(2)}%`;
};

export interface PartnerSummaryProps {
  api: BooksApi;
  version: number;
}

export function PartnerSummary({ api, version }: PartnerSummaryProps) {
  const [body, setBody] = useState<SummaryBody | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setBody(await api.summary());
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load the summary");
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load, version]);

  const download = async (path: string, filename: string) => {
    setBusy(true);
    try {
      const blob = await api.exportCsv(path);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not download it");
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <section className="books__panel">
        <p className="books__error" role="alert">
          {error}
        </p>
      </section>
    );
  }

  if (!body) {
    return (
      <section className="books__panel">
        <p className="books__hint">Loading…</p>
      </section>
    );
  }

  return (
    <section className="books__panel books__print">
      <header className="books__print-head">
        <h3 className="books__subhead">The partners' summary</h3>
        <p className="books__row-meta">As at {body.asOf}</p>
      </header>

      <h4 className="books__print-section">Spent to date</h4>
      <ul className="books__list">
        {body.spent.byCategory.map((row) => (
          <li key={row.categoryId} className="books__row">
            <span className="books__row-name">{row.name}</span>
            <span className="books__figure">₱{formatPesos(row.amount)}</span>
            <span className="books__row-meta">{KIND_NAMES[row.kind] ?? row.kind}</span>
          </li>
        ))}
      </ul>
      <p className="books__total">
        Spent altogether <strong>₱{formatPesos(body.spent.total)}</strong>
      </p>

      <h4 className="books__print-section">Where the money is</h4>
      <ul className="books__list">
        {body.position.places.map((place) => (
          <li key={place.placeId} className="books__row">
            <span className="books__row-name">{PLACE_NAMES[place.placeId]}</span>
            <span className="books__figure">₱{formatPesos(place.balance)}</span>
          </li>
        ))}
      </ul>
      <p className="books__total">
        In hand <strong>₱{formatPesos(body.position.total)}</strong>
        {body.position.uncountedShifts > 0 && (
          <span className="books__row-meta">
            {" "}
            · {body.position.uncountedShifts} shift
            {body.position.uncountedShifts === 1 ? "" : "s"} closed without a count, so their
            expected cash stands in
          </span>
        )}
      </p>

      <h4 className="books__print-section">The partners</h4>
      <ul className="books__list">
        {body.capital.partners.map((partner) => (
          <li key={partner.id} className="books__row">
            <span className="books__row-name">
              {partner.name}
              <span className="books__chip">{ownership(partner.ownershipBp)}</span>
            </span>
            <span className="books__figure">₱{formatPesos(partner.paid)}</span>
            <span className="books__row-meta">
              {partner.role ? `${partner.role} · ` : ""}of ₱{formatPesos(partner.subscribed)} subscribed
              {partner.inKind > 0 && `, ₱${formatPesos(partner.inKind)} in kind`}
              {partner.owed > 0 && ` · still owes ₱${formatPesos(partner.owed)}`}
            </span>
          </li>
        ))}
      </ul>
      <p className="books__total">
        Paid in <strong>₱{formatPesos(body.capital.totals.paid)}</strong> of ₱
        {formatPesos(body.capital.totals.subscribed)}
      </p>

      <h4 className="books__print-section">Still to spend</h4>
      <p className="books__total">
        <strong>₱{formatPesos(body.plan.openRemaining)}</strong> planned · ₱
        {formatPesos(body.plan.capitalStillNeeded)} more capital needed
        {body.plan.totalsAreAFloor && (
          <span className="books__row-meta">
            {" "}
            · {body.plan.unpricedLines} line{body.plan.unpricedLines === 1 ? "" : "s"} unpriced, so
            these totals are a floor
          </span>
        )}
      </p>

      <div className="books__buttons books__no-print">
        <button
          type="button"
          className="books__action"
          onClick={() => void download("/export/partners.csv", "partners.csv")}
          disabled={busy}
        >
          Partners CSV
        </button>
        <button
          type="button"
          className="books__action"
          onClick={() =>
            void download(
              `/export/expenses.csv?from=2020-01-01&to=${today()}`,
              `expenses-to-${today()}.csv`,
            )
          }
          disabled={busy}
        >
          Spending CSV
        </button>
        <button type="button" className="books__action is-primary" onClick={() => window.print()}>
          Print / save as PDF
        </button>
      </div>
    </section>
  );
}
